import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { toast } from '@/components/ui/toast-store'
import { DashboardUnavailable, dashboardsEnabled, useBoard } from '@/features/dashboard/api/queries'
import { useMitraEnabled } from '@/features/mitra/api/access'
import { useMitraStore } from '@/features/mitra/store'
import DashboardBody from '@/screens/dashboards/components/DashboardBody'
import { StudioGate } from '@/screens/dashboards/StudioGate'

export const Route = createFileRoute('/_app/dashboards/$dashboardId')({
  component: GatedDashboardScreen,
})

/** Only for a reader Mitra is open to (`StudioGate`). */
function GatedDashboardScreen() {
  return (
    <StudioGate>
      <DashboardScreen />
    </StudioGate>
  )
}

/** Sized against a row of figure cards and a table, per `CardState`'s own rule. */
const DASHBOARD_SKELETON = (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-1 gap-4 @md:grid-cols-2 @3xl:grid-cols-3">
      {Array.from({ length: 3 }, (_, index) => (
        <Skeleton key={index} className="h-33 rounded-lg" />
      ))}
    </div>
    <Skeleton className="h-55 w-full rounded-lg" />
  </div>
)

/**
 * One dashboard by the engine's id (`/v1/dashboards/{id}`): its measures
 * worked out every time the page is read. Two ways in, one store: Mitra
 * builds or changes it in the chat and the reader saves it there, or the
 * reader adds and edits here. A dashboard the reader no longer has says so.
 *
 * The page's header is the dashboard's own (its name, purpose and actions),
 * so once it has loaded `DashboardBody` draws the whole screen; until then
 * this draws a plain one over the skeleton.
 */
function DashboardScreen() {
  const { dashboardId } = Route.useParams()
  return <DashboardPage key={dashboardId} dashboardId={dashboardId} />
}

/** The page for one id; keyed by it, so another dashboard starts fresh. */
export function DashboardPage({ dashboardId }: { dashboardId: string }) {
  const query = useBoard(dashboardId)
  const openPanel = useMitraStore((state) => state.openPanel)
  const askMitra = useMitraEnabled()
    ? (question: string) => {
        openPanel(question)
      }
    : null
  const reason = query.error instanceof DashboardUnavailable ? query.error.reason : null
  const navigate = useNavigate()

  // DELETED WHILE OPEN (from the chat, or another tab): the read again
  // answers "missing" but React Query keeps the last body, so without this
  // the page would go on drawing a dashboard that no longer exists. Back to
  // the list, once, saying why.
  const gone = reason === 'missing' && query.data !== undefined
  const left = useRef(false)
  const goneName = query.data?.dashboard?.name ?? 'That dashboard'
  useEffect(() => {
    if (!gone || left.current) return
    left.current = true
    toast.info(`“${goneName}” was deleted.`)
    void navigate({ to: '/dashboards', replace: true })
  }, [gone, navigate, goneName])

  if (query.data && dashboardsEnabled() && !gone) {
    return (
      <DashboardBody
        body={query.data}
        boardId={dashboardId}
        onReload={() => {
          void query.refetch()
        }}
        onAskMitra={askMitra}
      />
    )
  }
  return (
    <ScreenLayout header={<TitleRow back="/dashboards" title="Dashboard" />} pageTitle="Dashboard">
      {!dashboardsEnabled() || reason === 'unavailable' ? (
        <EmptyState
          size="page"
          title="Dashboards are not available here"
          description="Your dashboards are kept by Mitra, which is not configured for this app yet."
        />
      ) : reason === 'missing' ? (
        <EmptyState
          size="page"
          title="That dashboard is no longer here"
          description="It may have been deleted, here or in another tab."
          // The way on where a link (Mitra's, or an old bookmark) named a
          // dashboard that is gone: the list of the ones the reader has.
          action={
            <Link to="/dashboards" className={stateActionClass('page')}>
              See your dashboards
            </Link>
          }
        />
      ) : (
        <QueryState
          query={query}
          skeleton={DASHBOARD_SKELETON}
          loadingLabel="Opening your dashboard…"
          size="page"
          failed={{ title: 'Your dashboard did not load' }}
        >
          {() => null}
        </QueryState>
      )}
    </ScreenLayout>
  )
}
